<template>
  <div class="auth-page">
    <!-- Background Video -->
    <div class="auth-background" :class="{ 'fallback-active': videoFailed }">
      <video
        v-if="!videoFailed"
        autoplay
        muted
        loop
        playsinline
        class="background-video"
        @error="onVideoError"
      >
  <source :src="videoSource" type="video/mp4">
      </video>
      <div class="video-overlay"></div>
    </div>

    <!-- Main Content -->
    <div class="auth-content">
      <v-container fluid class="auth-container">
        <v-row align="center" justify="center" class="min-height-screen">
          <v-col cols="12" sm="10" md="8" lg="6" xl="4">
            <!-- Language Switcher (Hidden on mobile - use header switcher) -->
            <div class="text-end mb-4 d-none d-sm-block">
              <LanguageSwitcher />
            </div>

            <!-- Auth Card -->
            <v-card 
              class="auth-card glass-effect"
              elevation="24"
            >
              <!-- Header -->
              <v-card-title class="auth-header text-center pa-6">
                <div class="auth-logo mb-4">
                  <v-img
                    :src="logoSrc"
                    alt="Көркем Авто"
                    max-width="80"
                    class="mx-auto"
                  />
                </div>
                <h2 class="text-h4 text-gradient">
                  {{ $t('auth.register') }}
                </h2>
                <p class="text-subtitle-1 mt-2 text-medium-emphasis">
                  Көркем Авто мектебіне қош келдіңіз
                </p>
              </v-card-title>

              <!-- Multi-step Form -->
              <v-card-text class="pa-6 pt-0">
                <!-- Step Indicator -->
                <v-stepper
                  v-model="currentStep"
                  flat
                  class="mb-6"
                  hide-actions
                >
                  <v-stepper-header>
                    <v-stepper-item
                      :value="1"
                      :complete="currentStep > 1"
                      :color="currentStep >= 1 ? 'primary' : 'grey'"
                    >
                      {{ $t('auth.personalInfo') }}
                    </v-stepper-item>
                    
                    <v-divider />
                    
                    <v-stepper-item
                      :value="2"
                      :complete="currentStep > 2"
                      :color="currentStep >= 2 ? 'primary' : 'grey'"
                    >
                      {{ $t('auth.instructor') }}
                    </v-stepper-item>
                    
                    <v-divider />
                    
                    <v-stepper-item
                      :value="3"
                      :color="currentStep >= 3 ? 'primary' : 'grey'"
                    >
                      {{ $t('common.confirm') }}
                    </v-stepper-item>
                  </v-stepper-header>
                </v-stepper>

                <v-form 
                  ref="registerForm"
                  v-model="formValid"
                  @submit.prevent="handleSubmit"
                >
                  <!-- Step 1: Personal Information -->
                  <div v-if="currentStep === 1" class="step-content">
                    <v-text-field
                      v-model="userData.fio"
                      :label="$t('auth.fullName')"
                      :rules="nameRules"
                      variant="outlined"
                      prepend-inner-icon="mdi-account"
                      class="mb-4"
                      clearable
                    />

                    <PhoneInput
                      v-model="userData.phone"
                      :label="$t('auth.phone')"
                      :rules="phoneRules"
                      required
                    />

                    <v-text-field
                      v-model="userData.email"
                      :label="$t('auth.email') + ' (' + $t('common.optional') + ')'"
                      :rules="emailRules"
                      variant="outlined"
                      prepend-inner-icon="mdi-email"
                      type="email"
                      class="mb-4"
                      clearable
                    />

                    <v-text-field
                      v-model="userData.password"
                      :label="$t('auth.password')"
                      :rules="passwordRules"
                      variant="outlined"
                      prepend-inner-icon="mdi-lock"
                      :type="showPassword ? 'text' : 'password'"
                      :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append-inner="showPassword = !showPassword"
                      class="mb-4"
                      clearable
                    />
                  </div>

                  <!-- Step 2: Instructor Selection -->
                  <div v-if="currentStep === 2" class="step-content">
                    <h3 class="text-h6 mb-4 text-center">
                      Инструкторды таңдаңыз
                    </h3>
                    
                    <div v-if="loadingInstructors" class="text-center py-8">
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                    
                    <v-radio-group
                      v-else
                      v-model="userData.instructorId"
                      class="instructor-selection"
                    >
                      <v-card
                        v-for="instructor in instructors"
                        :key="instructor.id"
                        class="instructor-option mb-3"
                        :class="{ 'selected': userData.instructorId === instructor.id }"
                        @click="userData.instructorId = instructor.id"
                      >
                        <v-card-text class="pa-3">
                          <div class="d-flex align-center">
                            <v-radio
                              :value="instructor.id"
                              class="me-3"
                            />
                            
                            <v-avatar
                              size="60"
                              class="me-3"
                            >
                              <v-img
                                :src="getInstructorPhotoUrl(instructor.photo)"
                                :alt="instructor.fio"
                              />
                            </v-avatar>
                            
                            <div class="flex-grow-1">
                              <h4 class="text-subtitle-1">{{ instructor.fio }}</h4>
                              <div class="text-body-2 text-medium-emphasis">
                                <v-icon size="small" class="me-1">mdi-car</v-icon>
                                {{ instructor.carModel }}
                              </div>
                              <div class="text-body-2 text-medium-emphasis">
                                <v-icon size="small" class="me-1">mdi-star</v-icon>
                                {{ instructor.rating.toFixed(1) }}
                              </div>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-radio-group>
                    
                    <v-btn
                      variant="text"
                      color="primary"
                      block
                      @click="userData.instructorId = null"
                    >
                      Кейінірек таңдаймын
                    </v-btn>
                  </div>

                  <!-- Step 3: Confirmation -->
                  <div v-if="currentStep === 3" class="step-content">
                    <h3 class="text-h6 mb-4 text-center">
                      Мәліметтерді растаңыз
                    </h3>
                    
                    <v-list class="confirmation-list bg-transparent">
                      <v-list-item>
                        <template #prepend>
                          <v-icon>mdi-account</v-icon>
                        </template>
                        <v-list-item-title>{{ userData.fio }}</v-list-item-title>
                        <v-list-item-subtitle>Толық аты-жөні</v-list-item-subtitle>
                      </v-list-item>
                      
                      <v-list-item>
                        <template #prepend>
                          <v-icon>mdi-phone</v-icon>
                        </template>
                        <v-list-item-title>{{ userData.phone }}</v-list-item-title>
                        <v-list-item-subtitle>Телефон нөмері</v-list-item-subtitle>
                      </v-list-item>
                      
                      <v-list-item v-if="userData.email">
                        <template #prepend>
                          <v-icon>mdi-email</v-icon>
                        </template>
                        <v-list-item-title>{{ userData.email }}</v-list-item-title>
                        <v-list-item-subtitle>Электронды пошта</v-list-item-subtitle>
                      </v-list-item>
                      
                      <v-list-item>
                        <template #prepend>
                          <v-icon>mdi-lock</v-icon>
                        </template>
                        <v-list-item-title>{{ '•'.repeat(userData.password.length) }}</v-list-item-title>
                        <v-list-item-subtitle>Құпия сөз</v-list-item-subtitle>
                      </v-list-item>
                      
                      <v-list-item v-if="selectedInstructor">
                        <template #prepend>
                          <v-icon>mdi-account-tie</v-icon>
                        </template>
                        <v-list-item-title>{{ selectedInstructor.fio }}</v-list-item-title>
                        <v-list-item-subtitle>Таңдалған инструктор</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                    
                    <v-alert
                      type="info"
                      variant="tonal"
                      class="mt-4"
                    >
                      <span v-if="selectedInstructor">
                        Инструкторға жазылған соң сіз чат арқылы байланыса аласыз және тест жүйесіне қол жетімділігіңіз шектелген болады.
                      </span>
                      <span v-else>
                        Тіркелгеннен кейін біздің маман сізбен жақын уақытта хабарласады.
                      </span>
                    </v-alert>
                  </div>

                  <!-- Error Alert -->
                  <v-alert
                    v-if="error"
                    type="error"
                    variant="tonal"
                    class="mb-4"
                    closable
                    @click:close="clearError"
                  >
                    {{ error }}
                  </v-alert>

                  <!-- Navigation Buttons -->
                  <div class="d-flex justify-space-between mt-6">
                    <v-btn
                      v-if="currentStep > 1"
                      variant="outlined"
                      @click="currentStep--"
                    >
                      <v-icon start>mdi-arrow-left</v-icon>
                      {{ $t('common.back') }}
                    </v-btn>
                    
                    <v-spacer v-if="currentStep === 1" />
                    
                    <v-btn
                      v-if="currentStep < 3"
                      color="primary"
                      :disabled="!canProceed"
                      @click="nextStep"
                    >
                      {{ $t('common.next') }}
                      <v-icon end>mdi-arrow-right</v-icon>
                    </v-btn>
                    
                    <v-btn
                      v-else
                      type="submit"
                      color="primary"
                      :loading="loading"
                      :disabled="!formValid"
                    >
                      <v-icon start>mdi-check</v-icon>
                      {{ $t('auth.registerButton') }}
                    </v-btn>
                  </div>

                  <!-- Login Link -->
                  <v-divider class="my-6">
                    <span class="text-medium-emphasis px-4">немесе</span>
                  </v-divider>

                  <div class="text-center">
                    <span class="text-medium-emphasis">
                      {{ $t('auth.hasAccount') }}
                    </span>
                    <v-btn
                      variant="text"
                      color="primary"
                      class="ml-2"
                      @click="$router.push('/login')"
                    >
                      {{ $t('auth.loginHere') }}
                    </v-btn>
                  </div>
                </v-form>
              </v-card-text>
            </v-card>

            <!-- Footer Links -->
            <div class="auth-footer text-center mt-6">
              <v-btn
                variant="text"
                size="small"
                @click="$router.push('/')"
              >
                <v-icon start>mdi-home</v-icon>
                {{ $t('nav.home') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import api from '@/utils/api'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import PhoneInput from '@/components/ui/PhoneInput.vue'
import { withBasePath } from '@/utils/path'

// Composables
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()
const snackbar = useSnackbarStore()

// Refs
const registerForm = ref(null)
const formValid = ref(false)
const currentStep = ref(1)
const instructors = ref([])
const loadingInstructors = ref(false)
const showPassword = ref(false)

const userData = ref({
  fio: '',
  phone: '',
  email: '',
  password: '',
  instructorId: null
})

// Background video fallback handling
const videoFailed = ref(false)
const onVideoError = () => {
  videoFailed.value = true
}

const videoSource = withBasePath('/videos/hero-video-2.mp4')
const logoSrc = withBasePath('/logo.svg')
const defaultInstructorPhoto = null

// Helper function for instructor photo URLs
const getInstructorPhotoUrl = (photo) => {
  if (!photo) return null
  
  // If it's a full URL, return as-is
  if (/^https?:\/\//i.test(photo)) return photo
  
  const sanitizedPath = photo.replace(/^\/+/, '')
  
  // Use API endpoint for images to bypass nginx static file issues
  if (sanitizedPath.startsWith('instructors/')) {
    return `/api/images/${sanitizedPath}`
  }
  return `/api/images/instructors/${sanitizedPath}`
}

// Computed
const loading = computed(() => authStore.loading)
const error = computed(() => authStore.error)

const selectedInstructor = computed(() => {
  return instructors.value.find(i => i.id === userData.value.instructorId)
})

const canProceed = computed(() => {
  switch (currentStep.value) {
    case 1:
      return userData.value.fio && userData.value.phone && userData.value.password &&
             phoneRules.every(rule => rule(userData.value.phone) === true) &&
             nameRules.every(rule => rule(userData.value.fio) === true) &&
             passwordRules.every(rule => rule(userData.value.password) === true)
    case 2:
      return true // Instructor selection is optional
    case 3:
      return formValid.value
    default:
      return false
  }
})

// Validation rules
const nameRules = [
  v => !!v || t('validation.required'),
  v => v.length >= 2 || t('validation.minLength', { min: 2 }),
  v => v.length <= 100 || t('validation.maxLength', { max: 100 })
]

const phoneRules = [
  v => !!v || t('validation.required'),
  v => {
    const digits = (v || '').replace(/\D/g, '')
    // Строго требуем +7 и ровно 11 цифр начиная с 7
    return (digits.length === 11 && digits.startsWith('7') && v.startsWith('+7')) || t('validation.phone')
  }
]

const emailRules = [
  v => !v || /.+@.+\..+/.test(v) || t('validation.email')
]

const passwordRules = [
  v => !!v || t('validation.required'),
  v => v.length >= 6 || t('validation.minLength', { min: 6 }),
  v => v.length <= 50 || t('validation.maxLength', { max: 50 })
]

// Methods
const loadInstructors = async () => {
  loadingInstructors.value = true
  try {
    // Используем публичный эндпоинт без авторизации
    const response = await api.get('/public/instructors')
    // Приводим данные к нужному формату, если потребуется
    instructors.value = (response.data || []).map((i) => ({
      id: i.id,
      fio: i.fio,
      photo: i.photo,
      carModel: i.carModel,
      rating: typeof i.rating === 'number' ? i.rating : Number(i.rating) || 0,
    }))
    
    // Auto-select instructor from query param
    const instructorId = route.query.instructor
    if (instructorId) {
      userData.value.instructorId = parseInt(instructorId)
    }
  } catch (error) {
    // Error loading instructors - use fallback
    instructors.value = [
      { id: 1, fio: 'Әлия Нұрғалиева', carModel: 'Hyundai Accent', rating: 4.9, photo: null },
      { id: 2, fio: 'Дархан Сапаров', carModel: 'Toyota Corolla', rating: 4.8, photo: null },
      { id: 3, fio: 'Гүлнара Әбілқайырова', carModel: 'Kia Rio', rating: 4.7, photo: null },
    ]
  } finally {
    loadingInstructors.value = false
  }
}

const nextStep = () => {
  if (canProceed.value && currentStep.value < 3) {
    currentStep.value++
    
    // Load instructors when moving to step 2
    if (currentStep.value === 2 && instructors.value.length === 0) {
      loadInstructors()
    }
  }
}

const handleSubmit = async () => {
  if (!formValid.value) return

  authStore.loading = true
  authStore.error = null

  try {
    // Normalize phone number before sending - same logic as other auth forms
    const originalPhone = userData.value.phone
    let normalizedPhone = originalPhone.replace(/\D/g, '')
    
    if (normalizedPhone.startsWith('8') && normalizedPhone.length === 11) {
      normalizedPhone = '7' + normalizedPhone.slice(1)
    }
    
    // Add + prefix for backend consistency
    const phoneWithPrefix = `+${normalizedPhone}`

    const payload = {
      fio: (userData.value.fio || '').trim(),
      phone: phoneWithPrefix,
      email: (userData.value.email || '').trim() || null,
      password: userData.value.password,
      instructorId: userData.value.instructorId || null,
    }

    let response
    if (userData.value.instructorId) {
      // Инструкторға жазылу - LIMITED_STUDENT ретінде тіркелу
      response = await api.post('/auth/register-for-instructor', payload)
    } else {
      // Қарапайым тіркелу - STUDENT ретінде
      response = await authStore.register(payload)
    }
    
    // Show success message
    const successMessage = userData.value.instructorId ? 
      'Инструкторға сәтті жазылдыңыз! Енді сіз чат арқылы байланыса аласыз.' :
      t('messages.registerSuccess')
    
    snackbar.success(successMessage)
    
    // Автоматты түрде кіру
    try {
      await authStore.login({
        phone: phoneWithPrefix,
        password: userData.value.password
      })

      const user = authStore.user
      const wantsTelegram = route.query.telegram === '1' || userData.value.instructorId

      // For students who came via "Enroll" — open Telegram bot link
      if (wantsTelegram && user && ['STUDENT', 'LIMITED_STUDENT'].includes(user.role)) {
        try {
          const tgRes = await api.post('/student/telegram-link')
          if (tgRes.data?.link) {
            window.open(tgRes.data.link, '_blank', 'noopener,noreferrer')
          }
        } catch (_) { /* non-fatal */ }
      }

      // Redirect to appropriate dashboard based on role
      if (user) {
        const dashboardPath = user.role === 'ADMIN' ? '/admin/dashboard' :
                             user.role === 'INSTRUCTOR' ? '/instructor/dashboard' :
                             user.role === 'THEORY_TEACHER' ? '/teacher/dashboard' :
                             '/student/dashboard'
        router.push(dashboardPath)
      } else {
        router.push('/student/dashboard')
      }
    } catch (loginError) {
      // Жүйеге кіру қатесі болса, логин бетіне жіберу
      router.push({
        path: '/login',
        query: { 
          message: 'registration_success',
          phone: userData.value.phone 
        }
      })
    }
    
  } catch (err) {
    // Показать ошибку пользователю
    const msg = err.response?.data?.error || authStore.error || t('auth.registerForm.registerError')
    snackbar.error(msg)
    authStore.error = msg
  } finally {
    authStore.loading = false
  }
}

const clearError = () => {
  authStore.clearError()
}

// Lifecycle
onMounted(() => {
  // Clear any previous errors
  authStore.clearError()
  
  // Check if instructor is preselected
  if (route.query.instructor) {
    // Don't skip to step 2, let user fill step 1 first
    // Just preload instructors and set instructor ID
    loadInstructors()
    userData.value.instructorId = parseInt(route.query.instructor)
  }
})
</script>

<style scoped>
.auth-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
}

.auth-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.background-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

/* Fallback: static gradient background when video fails */
.auth-background.fallback-active {
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #42a5f5 100%);
}

.auth-content {
  position: relative;
  z-index: 1;
  width: 100%;
}

.auth-container {
  min-height: 100vh;
  padding: 2rem 1rem;
}

.min-height-screen {
  min-height: 100vh;
}

.auth-card {
  border-radius: 24px;
  border: 1px solid rgba(255, 193, 7, 0.3);
  animation: slideInUp 0.6s ease-out;
  max-width: 100%;
}

.auth-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.step-content {
  min-height: 300px;
}

.instructor-selection {
  max-height: 400px;
  overflow-y: auto;
}

.instructor-option {
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
}

.instructor-option:hover {
  border-color: rgba(255, 193, 7, 0.3);
}

.instructor-option.selected {
  border-color: #FFC107;
  background: rgba(255, 193, 7, 0.1);
}

.confirmation-list .v-list-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .auth-container {
    padding: 1rem 0.5rem;
  }
  
  .auth-card {
    margin: 0 0.5rem;
  }
  
  .step-content {
    min-height: 250px;
  }
  
  .instructor-selection {
    max-height: 300px;
  }
}

@media (max-width: 480px) {
  .text-h4 {
    font-size: 1.75rem !important;
  }
  
  .auth-logo img {
    max-width: 60px;
  }
  
  .v-stepper-header {
    flex-direction: column;
  }
  
  .v-stepper-item {
    margin-bottom: 0.5rem;
  }
}
</style>